"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { notFound } from "next/navigation";
import { Check, ShoppingCart, ChevronDown } from "lucide-react";
import { useCart } from "@/store/cart";
import { formatPrice } from "@/lib/utils";
import { chipStyle } from "@/components/configurator/chip-style";
import { ColorSwatch } from "@/components/shop/ColorSwatch";
import { ProductImagePlaceholder } from "@/components/shop/ProductImagePlaceholder";
import { parseStringProduct } from "@/lib/catalog/shopify-catalog";
import { formatGaugePipeList } from "@/lib/catalog/gauge-format";
import { CONTACT, FULFILMENT } from "@/lib/content/business";
import { CONSTRUCTION_LABEL } from "@/lib/configurator/preset-sets";
import { buildConfigureHrefFromProduct } from "@/lib/products/configure-link";
import {
  AVAILABILITY_DETAIL,
  AVAILABILITY_LABEL,
  variantAvailability,
} from "@/lib/products/availability";
import { isPurchaseBlocked, variantPurchasable } from "@/lib/products/purchase-eligibility";
import { GaugeInfo } from "@/components/common/GaugeInfo";
import { auroraLineBgClass } from "@/lib/catalog/line-family";
import { cn } from "@/lib/utils";
import {
  findVariantForSelection,
  getDefaultPurchasableVariant,
  getDisabledOptionValues,
  getSelectedOptions,
} from "@/lib/products/variant-options";
import type { ShopifyProduct } from "@/types/shopify";

// ─── Hooks ────────────────────────────────────────────────

function useProduct(handle: string, initialProduct: ShopifyProduct | null) {
  const [product, setProduct] = useState<ShopifyProduct | null>(initialProduct);
  const [loading, setLoading] = useState(!initialProduct);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (initialProduct && retryKey === 0) return;

    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setError(false);
      setMissing(false);

      try {
        const response = await fetch(`/api/products/${handle}`);
        if (cancelled) return;

        if (response.status === 404) {
          setProduct(null);
          setMissing(true);
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to load product");
        }

        const data = await response.json();
        if (cancelled) return;
        setProduct(data?.product ?? null);
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadProduct();

    return () => {
      cancelled = true;
    };
  }, [handle, initialProduct, retryKey]);

  return {
    product,
    loading,
    missing,
    error,
    retry: () => setRetryKey((value) => value + 1),
  };
}

interface SiblingProduct {
  handle: string;
  gaugeName: string;
  individualGauges: string;
  availableForSale: boolean;
  /** Colour option values this sibling actually offers. */
  colors?: string[];
}

function useSiblings(handle: string, productType: string | null, initialSiblings: SiblingProduct[]) {
  const [siblings, setSiblings] = useState<SiblingProduct[]>(initialSiblings);

  useEffect(() => {
    if (initialSiblings.length > 0) return;

    if (!productType) return;
    fetch(
      `/api/products/siblings?handle=${encodeURIComponent(handle)}&productType=${encodeURIComponent(productType)}`
    )
      .then((r) => (r.ok ? r.json() : { siblings: [] }))
      .then((data) => setSiblings(data.siblings ?? []))
      .catch(() => {});
  }, [handle, initialSiblings, productType]);

  return siblings;
}

// ─── Component ────────────────────────────────────────────

interface Props {
  handle: string;
  initialProduct?: ShopifyProduct | null;
  initialSiblings?: SiblingProduct[];
}

/** Reads ?color= from the URL, so it needs a Suspense boundary. */
export function ProductPageClient(props: Props) {
  return (
    <Suspense fallback={<div className="min-h-[70vh]" aria-busy="true" />}>
      <ProductPageClientInner {...props} />
    </Suspense>
  );
}

function ProductPageClientInner({ handle, initialProduct = null, initialSiblings = [] }: Props) {
  const { product, loading, missing, error, retry } = useProduct(handle, initialProduct);
  const { addItem } = useCart();
  const siblings = useSiblings(handle, product?.productType ?? null, initialSiblings);
  const searchParams = useSearchParams();
  // A gauge change navigates to a sibling product and carries the chosen
  // colour in ?color=. Without this the customer silently landed on the
  // default variant of the new gauge.
  const requestedColor = searchParams.get("color");

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [optionNotice, setOptionNotice] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>("details");

  if (loading) {
    return (
      <div className="container-main py-8 pb-28 md:pb-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="aspect-square rounded-md bg-[#141414]" />
          <div className="space-y-4">
            <div className="h-4 w-24 rounded bg-[#1E1E1E]" />
            <div className="h-10 w-4/5 rounded bg-[#141414]" />
            <div className="h-7 w-32 rounded bg-[#141414]" />
            <div className="h-12 w-full rounded bg-[#141414]" />
            <div className="h-12 w-full rounded bg-[#141414]" />
            <div className="h-14 w-full rounded bg-[#E8C54722]" />
          </div>
        </div>
      </div>
    );
  }

  if (missing) {
    notFound();
  }

  if (error) {
    return (
      <div className="container-main py-20">
        <div
          className="mx-auto max-w-md rounded-md border p-6 text-center"
          style={{ backgroundColor: "#141414", borderColor: "#2A2A2A" }}
        >
          <p className="text-lg font-bold mb-2">We couldn&apos;t load this product.</p>
          <p className="text-sm mb-4" style={{ color: "#9A9A9A" }}>
            This looks like a temporary network problem. Please try again.
          </p>
          <button
            type="button"
            onClick={retry}
            className="px-5 py-2.5 text-sm font-bold rounded-sm transition-all hover:brightness-110"
            style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    notFound();
  }

  const meta = parseStringProduct(product);
  // One shared decision — the same one product cards, /api/cart/add and the
  // product structured data use.
  const purchaseBlocked = isPurchaseBlocked(meta);
  const variants = product.variants.nodes;

  // Resolve the colour carried over from a gauge change, if this set offers it.
  const carriedVariant = requestedColor
    ? variants.find((v) =>
        v.selectedOptions.some(
          (o) => o.name.toLowerCase() === "color" && o.value === requestedColor,
        ),
      )
    : undefined;

  // A colour was carried over but this gauge does not offer it. Explain that
  // and make the customer choose — never silently swap in another variant.
  const colorCarryFailed = !!requestedColor && !carriedVariant && !selectedVariantId;

  const selectedVariant = colorCarryFailed
    ? null
    : variants.find((variant) => variant.id === selectedVariantId) ??
      carriedVariant ??
      getDefaultPurchasableVariant(variants);
  const images = product.images.nodes;
  const colorOption = product.options.find((o) => o.name.toLowerCase() === "color");
  const otherOptions = product.options.filter((o) => o.name.toLowerCase() !== "color");
  const selectedOptions = getSelectedOptions(selectedVariant);

  function selectOption(optionName: string, value: string) {
    const newVariant = findVariantForSelection(variants, selectedOptions, optionName, value);

    // Explain instead of ignoring the click. Silently doing nothing left the
    // customer unsure whether the choice registered.
    if (!newVariant) {
      setOptionNotice(`We don't build this set in ${value}.`);
      return;
    }
    if (!newVariant.availableForSale) {
      setOptionNotice(`${value} is out of stock for this gauge.`);
      return;
    }

    setOptionNotice(null);
    setAddError(null);
    setSelectedVariantId(newVariant.id);
    const imgIndex = images.findIndex((img) => img.url === newVariant.image?.url);
    if (imgIndex >= 0) setSelectedImageIndex(imgIndex);
  }

  async function handleAddToCart() {
    if (purchaseBlocked || !selectedVariant || adding || !purchasable) return;
    setAdding(true);
    setAddError(null);
    try {
      await addItem(selectedVariant.id, 1);
    } catch {
      // The add used to fail silently — no message, no retry, cart unchanged.
      setAddError("We couldn't add that to your cart. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  // Price follows the selected variant. priceRange.minVariantPrice is only a
  // placeholder for "nothing selected yet" and is labelled as a from-price.
  const price = selectedVariant?.price ?? product.priceRange.minVariantPrice;
  const showingFromPrice = !selectedVariant;
  const compareAtPrice = selectedVariant?.compareAtPrice;
  const purchasable = variantPurchasable(meta, selectedVariant);
  const availability = variantAvailability(product, selectedVariant);
  const configureLink = buildConfigureHrefFromProduct(meta, selectedOptions["Color"]);

  const productLineSlug = parseStringProduct(product).productLineSlug;
  const lineBgClass = auroraLineBgClass(productLineSlug);

  return (
    <div style={{ backgroundColor: "#0A0A0A", minHeight: "70vh" }}>
      <div className="container-main py-8 pb-28 md:pb-8">
        {/* Breadcrumb */}
        <nav className="text-xs mb-6" style={{ color: "#8A8A8A" }}>
          <Link href="/" className="hover:text-[#9A9A9A] transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link href="/shop" className="hover:text-[#9A9A9A] transition-colors">
            Shop
          </Link>
          <span className="mx-2">/</span>
          <span style={{ color: "#9A9A9A" }}>{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="flex flex-col gap-3">
            <div
              className="relative aspect-square rounded-md overflow-hidden"
              style={{ backgroundColor: "#141414" }}
            >
              {images[selectedImageIndex] ? (
                <Image
                  src={images[selectedImageIndex].url}
                  alt={images[selectedImageIndex].altText ?? product.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  loading="eager"
                />
              ) : (
                <ProductImagePlaceholder />
              )}
            </div>

            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImageIndex(i)}
                    className="relative w-16 h-16 rounded flex-shrink-0 overflow-hidden border-2 transition-colors"
                    style={{
                      borderColor: i === selectedImageIndex ? "#E8C547" : "#2A2A2A",
                      backgroundColor: "#141414",
                    }}
                  >
                    <Image
                      src={img.url}
                      alt={img.altText ?? ""}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          {/* The product line reads as a thin accent strip, not a full-panel
              gradient. The wash used to sit behind the price, swatches and the
              whole specification table, which made the secondary text and the
              selected-swatch ring hard to read. */}
          <div className="relative rounded-md">
            {lineBgClass && (
              <span
                className={cn(lineBgClass, "relative mb-5 block h-1 w-full overflow-hidden rounded-full")}
                aria-hidden
              />
            )}
            {/* The product line, not Shopify's vendor field — vendor still says
                "Aurora Premium" on records whose line is Aurora One, which
                contradicted the Product line row further down the page. */}
            {/* Suppressed for a conflicted record: the product line is derived
                from the handle, and the handle disagreeing with the title is
                precisely the unresolved question. A confident badge would
                assert the disputed answer. */}
            {!purchaseBlocked && (meta.productLine || product.vendor) && (
              <span
                className="inline-block px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded mb-3"
                style={{
                  backgroundColor: "#E8C54720",
                  color: "#E8C547",
                  border: "1px solid #E8C54740",
                }}
              >
                {meta.productLine || product.vendor}
              </span>
            )}

            <h1
              className="text-2xl sm:text-3xl font-black leading-tight mb-1"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {product.title}
            </h1>

            {/* Sibling gauge chips */}
            {siblings.length > 1 && (
              <div className="mb-4 mt-3">
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-2"
                  style={{ color: "#8A8A8A" }}
                >
                  Gauge
                </p>
                <div className="flex flex-wrap gap-2">
                  {siblings.map((s) => {
                    const isActive = s.handle === handle;
                    const currentColor = selectedOptions["Color"];
                    // Carry the colour over so changing gauge keeps the rest of
                    // the choice intact.
                    const href = currentColor
                      ? `/products/${s.handle}?color=${encodeURIComponent(currentColor)}`
                      : `/products/${s.handle}`;
                    return (
                      <Link
                        key={s.handle}
                        href={href}
                        aria-current={isActive ? "true" : undefined}
                        className="px-3 py-1.5 text-xs font-medium rounded-sm border transition-all"
                        style={{
                          ...chipStyle(isActive),
                          backgroundColor: isActive ? "#E8C54715" : "transparent",
                        }}
                      >
                        {s.gaugeName || s.individualGauges || s.handle}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-baseline gap-3 my-4">
              <span className="text-2xl font-bold" style={{ color: "#E8C547" }}>
                {showingFromPrice ? "From " : ""}
                {formatPrice(price.amount)}
              </span>
              {compareAtPrice &&
                parseFloat(compareAtPrice.amount) > parseFloat(price.amount) && (
                  <span className="text-base line-through" style={{ color: "#8A8A8A" }}>
                    {formatPrice(compareAtPrice.amount)}
                  </span>
                )}
            </div>

            {colorOption && (
              <div className="mb-5">
                <p className="text-sm font-semibold mb-2">
                  Color:{" "}
                  <span style={{ color: "#9A9A9A" }}>
                    {selectedOptions["Color"] ?? "Select"}
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {colorOption.values.map((val) => {
                    const disabled = getDisabledOptionValues(
                      variants,
                      selectedOptions,
                      "Color",
                      [val]
                    ).length > 0;
                    return (
                      <ColorSwatch
                        key={val}
                        name={val}
                        hexCode={null}
                        selected={selectedOptions["Color"] === val}
                        disabled={disabled}
                        size="lg"
                        onClick={() => selectOption("Color", val)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {otherOptions.map((option) => (
              <div key={option.id} className="mb-5">
                <p className="text-sm font-semibold mb-2">
                  {option.name}:{" "}
                  <span style={{ color: "#9A9A9A" }}>
                    {selectedOptions[option.name] ?? "Select"}
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {option.values.map((val) => {
                    const isSelected = selectedOptions[option.name] === val;
                    const disabled = getDisabledOptionValues(
                      variants,
                      selectedOptions,
                      option.name,
                      [val]
                    ).length > 0;
                    return (
                      <button
                        key={val}
                        onClick={() => selectOption(option.name, val)}
                        disabled={disabled}
                        className="px-3 py-1.5 text-xs font-medium rounded-sm border transition-all disabled:opacity-35 disabled:cursor-not-allowed"
                        style={{
                          ...chipStyle(isSelected),
                          backgroundColor: isSelected ? "#E8C54715" : "#141414",
                        }}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Availability / selection messages, above the CTA that they gate */}
            {colorCarryFailed && (
              <p
                className="mb-3 rounded-sm border px-3 py-2 text-xs"
                style={{ borderColor: "#E8C54755", backgroundColor: "#E8C5470F", color: "#E0E0E0" }}
                role="status"
              >
                This gauge isn&apos;t offered in {requestedColor}. Choose a color to continue.
              </p>
            )}
            {optionNotice && (
              <p
                className="mb-3 rounded-sm border px-3 py-2 text-xs"
                style={{ borderColor: "#E8404055", backgroundColor: "#E840400F", color: "#E0E0E0" }}
                role="status"
              >
                {optionNotice}
              </p>
            )}
            {/* The single explanation for a blocked record. It used to be
                repeated verbatim here, on the button, and in the Gauges row. */}
            {purchaseBlocked && (
              <div
                className="mb-3 rounded-sm border px-3 py-3 text-xs"
                style={{ borderColor: "#2A2A2A", backgroundColor: "#101010", color: "#B8B8B8" }}
                role="status"
              >
                <p className="font-semibold mb-1" style={{ color: "#F5F5F5" }}>
                  Specification under review
                </p>
                <p>
                  This listing describes two different sets, so we can&apos;t confirm
                  which one it is. It can&apos;t be ordered until that is resolved.
                </p>
                <p className="mt-2">
                  <Link href="/contact" className="underline hover:text-[#E8C547]">
                    Contact us to confirm the set you need
                  </Link>
                </p>
              </div>
            )}
            {!purchaseBlocked && !colorCarryFailed && availability !== "in-stock" && (
              <p
                className="mb-3 rounded-sm border px-3 py-2 text-xs"
                style={{ borderColor: "#2A2A2A", backgroundColor: "#101010", color: "#B8B8B8" }}
                role="status"
              >
                <strong>{AVAILABILITY_LABEL[availability]}.</strong>{" "}
                {AVAILABILITY_DETAIL[availability]}
              </p>
            )}
            {addError && (
              <p
                className="mb-3 rounded-sm border px-3 py-2 text-xs"
                style={{ borderColor: "#E8404055", backgroundColor: "#E840400F", color: "#E0E0E0" }}
                role="alert"
              >
                {addError}
              </p>
            )}

            <button
              onClick={handleAddToCart}
              disabled={!purchasable || adding}
              className="hidden w-full items-center justify-center gap-3 py-4 text-sm font-bold rounded-sm transition-all hover:brightness-110 disabled:opacity-50 mt-2 md:flex"
              style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
            >
              {adding ? (
                <span className="w-4 h-4 border-2 border-[#0A0A0A30] border-t-[#0A0A0A] rounded-full animate-spin" />
              ) : (
                <ShoppingCart size={16} />
              )}
              {purchaseBlocked
                ? "Specification under review"
                : adding
                ? "Adding…"
                : purchasable
                  ? "Add to Cart"
                  : colorCarryFailed
                    ? "Choose a color"
                    : AVAILABILITY_LABEL[availability]}
            </button>

            <div
              className="fixed inset-x-0 bottom-0 z-40 border-t bg-[#141414]/95 px-4 py-3 backdrop-blur-md md:hidden"
              style={{ borderColor: "#2A2A2A", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
            >
              <div className="mx-auto flex max-w-[var(--width-container)] items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs truncate" style={{ color: "#8A8A8A" }}>
                    {purchaseBlocked ? "Specification under review" : AVAILABILITY_LABEL[availability]}
                  </p>
                  <p className="text-lg font-bold" style={{ color: "#E8C547" }}>
                    {showingFromPrice ? "From " : ""}
                    {formatPrice(price.amount)}
                  </p>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={!purchasable || adding}
                  className="flex items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-bold transition-all hover:brightness-110 disabled:opacity-50"
                  style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
                >
                  {adding ? (
                    <span className="w-4 h-4 border-2 border-[#0A0A0A30] border-t-[#0A0A0A] rounded-full animate-spin" />
                  ) : (
                    <ShoppingCart size={16} />
                  )}
                  Add to cart
                </button>
              </div>
            </div>

            {/* Only facts with an approved source. Shipping thresholds, dispatch
                windows and return terms are not confirmed — see
                lib/content/business.ts (FULFILMENT). */}
            <div className="flex flex-wrap gap-4 mt-4">
              {[
                ...(purchaseBlocked || !meta.stringCount ? [] : [`${meta.stringCount}-string set`]),
              ].map((t) => (
                <div
                  key={t}
                  className="flex items-center gap-1.5 text-xs"
                  style={{ color: "#8A8A8A" }}
                >
                  <Check size={14} style={{ color: "#40C070" }} /> {t}
                </div>
              ))}
            </div>

            <div className="mt-8 border-t" style={{ borderColor: "#2A2A2A" }}>
              {[
                { id: "details", label: "Product Details", content: product.description },
                {
                  id: "shipping",
                  label: "Shipping & Returns",
                  content: FULFILMENT.hasApprovedFulfilmentTerms
                    ? ""
                    : `For shipping options, delivery estimates, or a return, contact us at ${CONTACT.email} or ${CONTACT.phone}.`,
                },
              ].filter((section) => section.content).map((section) => (
                <div key={section.id} className="border-b" style={{ borderColor: "#2A2A2A" }}>
                  <button
                    onClick={() =>
                      setOpenSection(openSection === section.id ? null : section.id)
                    }
                    aria-expanded={openSection === section.id}
                    aria-controls={`section-${section.id}`}
                    className="flex items-center justify-between w-full py-4 text-sm font-semibold text-left"
                  >
                    {section.label}
                    <ChevronDown
                      size={16}
                      className="transition-transform duration-200"
                      style={{
                        transform:
                          openSection === section.id ? "rotate(180deg)" : "rotate(0deg)",
                        color: "#9A9A9A",
                      }}
                    />
                  </button>
                  {openSection === section.id && (
                    <p
                      id={`section-${section.id}`}
                      className="pb-4 text-sm leading-relaxed"
                      style={{ color: "#9A9A9A" }}
                    >
                      {section.content}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Specifications — only fields with a source. Anything Shopify
                doesn't carry is omitted rather than filled with a per-line
                default (which used to label nylon classical sets as
                "Nickel-Wound" plain steel). */}
            <div className="mt-6 pt-6 border-t" style={{ borderColor: "#2A2A2A" }}>
              <h3 className="text-sm font-semibold mb-4">Specifications</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-xs">
                <dt style={{ color: "#B8B8B8" }}>Instrument</dt>
                <dd style={{ color: "#E0E0E0" }}>{meta.instrument}</dd>

                {!purchaseBlocked && meta.productLine && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Product line</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.productLine}</dd>
                  </>
                )}

                {/* String count is its own row — it is not a gauge. */}
                {!purchaseBlocked && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>String count</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.stringCount}</dd>
                  </>
                )}

                {purchaseBlocked && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Gauges</dt>
                    <dd style={{ color: "#B8B8B8" }}>Not established</dd>
                  </>
                )}

                {!purchaseBlocked && meta.gaugeName && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>
                      {meta.category === "classical"
                        ? "Tension"
                        : meta.category === "ukulele"
                          ? "Size"
                          : "Gauge"}
                    </dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.gaugeName}</dd>
                  </>
                )}

                {meta.individualGauges && (
                  <>
                    <dt className="flex items-center gap-1.5" style={{ color: "#B8B8B8" }}>
                      Gauges <GaugeInfo />
                    </dt>
                    <dd className="font-mono" style={{ color: "#E0E0E0" }}>
                      {formatGaugePipeList(meta.individualGauges, meta.category)}
                    </dd>
                  </>
                )}

                {meta.construction && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Construction</dt>
                    <dd style={{ color: "#E0E0E0" }}>
                      {meta.construction
                        .map((c, i) => `${i + 1}. ${CONSTRUCTION_LABEL[c]}`)
                        .join(" · ")}
                    </dd>
                  </>
                )}

                {meta.material && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Material</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.material}</dd>
                  </>
                )}

                {meta.coating && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Coating</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.coating}</dd>
                  </>
                )}

                {meta.tension && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Tension</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.tension}</dd>
                  </>
                )}

                {meta.recommendedTuning && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Tuning</dt>
                    <dd style={{ color: "#E0E0E0" }}>{meta.recommendedTuning}</dd>
                  </>
                )}

                {selectedVariant && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>Finish</dt>
                    <dd style={{ color: "#E0E0E0" }}>
                      {selectedOptions["Color"] ?? selectedVariant.title}
                    </dd>
                  </>
                )}

                {/* Real SKU. This row used to print the variant title (the
                    colour name) under a "SKU" label. */}
                {selectedVariant?.sku && (
                  <>
                    <dt style={{ color: "#B8B8B8" }}>SKU</dt>
                    <dd style={{ color: "#E0E0E0", fontFamily: "monospace" }}>
                      {selectedVariant.sku}
                    </dd>
                  </>
                )}
              </dl>

              {!purchaseBlocked && configureLink.exact && (
                <Link
                  href={configureLink.href}
                  className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold"
                  style={{ color: "#E8C547" }}
                >
                  Customize this set
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
